import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { CSSTransition } from 'react-transition-group';
import * as usersActions from '../../../redux/actions/usersActions';
import * as institutionsActions from '../../../redux/actions/institutionsActions';
import permissionsOptions, { userIsAdmin } from '../../../utils/permissionsOptions';

import InputErrors from '../../common/InputErrors';
import SoftAlert from '../../common/SoftAlert';
import AccountInfoItem from './AccountInfoItem';
import NotificationItem from './NotificationItem';
import EditIcon from '../../icons/EditIcon';
import InstitutionCard from './InstitutionCard';
import LoadingButton from '../../common/LoadingButton';

class AccountCards extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditingInstitutionCard: false,
      isEditingPersonalCard: false,
      serverErrors: [],
      institutionErrors: [],
      personalErrors: [],
      showSoftAlert: false,
      loadingPersonalEdits: false,
      loadingInstitutionEdits: false,
      temporaryValues: {
        institution: {},
        user: {},
      },
    };
  }

  toggleEditInstitutionCard = () => {
    this.setState(prevState => ({ isEditingInstitutionCard: !prevState.isEditingInstitutionCard }));
  }

  toggleEditPersonalCard = () => {
    this.setState(prevState => ({ isEditingPersonalCard: !prevState.isEditingPersonalCard }));
  }

  handleNotificationChange = (e) => {
    const { dispatchUsersActions, currentUserId } = this.props;
    dispatchUsersActions.updateUser({
      userId: currentUserId,
      notificationPreferences: {
        [e.target.name]: e.target.type === 'checkbox'
          ? e.target.checked
          : e.target.value,
      },
    }, this.onNotificationEditSuccess, this.onNotificationEditFailure);
  }

  onNotificationEditSuccess = () => {
    //
  }

  onNotificationEditFailure = (error) => {
    // Override any previous errors' timeouts
    clearTimeout(this.hideSoftAlertTimeout);
    clearTimeout(this.clearSoftAlertDataTimeout);
    this.setState({
      serverErrors: error.response
        ? error.response.data
        : ['There was a problem connecting to the server.'],
      showSoftAlert: true,
    });
    this.hideSoftAlertTimeout = setTimeout(() => {
      // Begin hiding the alert after 2 seconds
      this.setState({ showSoftAlert: false });
      this.clearSoftAlertDataTimeout = setTimeout(() => {
        // Clear the data after the transition is done
        this.setState({ serverErrors: [] });
      }, 200);
    }, 2000);
  }

  handleInstitutionInputChange = (e) => {
    const { name, value } = e.target;
    this.setState(prevState => ({
      temporaryValues: {
        ...prevState.temporaryValues,
        institution: {
          ...prevState.temporaryValues.institution,
          [name]: value,
        },
      },
    }));
  }

  handleUserInputChange = (e) => {
    const { name, value } = e.target;
    this.setState(prevState => ({
      temporaryValues: {
        ...prevState.temporaryValues,
        user: {
          ...prevState.temporaryValues.user,
          [name]: value,
        },
      },
    }));
  }

  cancelInstitutionEdits = () => {
    this.setState(prevState => ({
      isEditingInstitutionCard: false,
      temporaryValues: {
        ...prevState.temporaryValues,
        institution: {},
      },
    }));
    this.setState({ institutionErrors: [] });
  }

  saveInstitutionEdits = () => {
    const { dispatchInstitutionsActions, institution } = this.props;
    const { temporaryValues } = this.state;
    dispatchInstitutionsActions.updateInstitution({
      ...temporaryValues.institution,
      currentInstitutionId: institution.id,
    }, this.onInstitutionEditSuccess, this.onInstitutionEditFailure);
    this.setState({
      institutionErrors: [],
      loadingInstitutionEdits: true,
    });
  }

  onInstitutionEditSuccess = () => {
    this.setState({
      isEditingInstitutionCard: false,
      loadingInstitutionEdits: false,
    });
  }

  onInstitutionEditFailure = (error) => {
    this.setState({
      institutionErrors: error.response
        ? error.response.data
        : ['There was a problem connecting to the server.'],
      loadingInstitutionEdits: false,
    });
  }

  cancelPersonalEdits = () => {
    this.setState(prevState => ({
      isEditingPersonalCard: false,
      temporaryValues: {
        ...prevState.temporaryValues,
        user: {},
      },
    }));
    this.setState({ personalErrors: [] });
  }

  savePersonalEdits = () => {
    const { dispatchUsersActions, currentUserId } = this.props;
    const { temporaryValues } = this.state;
    dispatchUsersActions.updateUser({
      ...temporaryValues.user,
      userId: currentUserId,
    }, this.onPersonalEditSuccess, this.onPersonalEditFailure);
    this.setState({
      personalErrors: [],
      loadingPersonalEdits: true,
    });
  }

  onPersonalEditSuccess = () => {
    this.setState({
      isEditingPersonalCard: false,
      loadingPersonalEdits: false,
    });
  }

  onPersonalEditFailure = (error) => {
    this.setState({
      personalErrors: error.response
        ? error.response.data
        : ['There was a problem connecting to the server.'],
      loadingPersonalEdits: false,
    });
  }

  render() {
    const { user, institution, toggleChangePasswordModal } = this.props;
    const { serverErrors, personalErrors, institutionErrors, isEditingPersonalCard, isEditingInstitutionCard, temporaryValues, showSoftAlert, loadingInstitutionEdits } = this.state;
    const isAdmin = userIsAdmin(user);
    const permission = permissionsOptions.filter(item => user.roleId === parseInt(item.value, 10))[0];
    const permissionLabel = permission ? permission.label : 'Super Admin';

    return (
      <div className="account__cards">
        {isAdmin && (
          <InstitutionCard
            institution={institution}
            toggleEdit={this.toggleEditInstitutionCard}
            isEditing={isEditingInstitutionCard}
            handleChange={this.handleInstitutionInputChange}
            temporaryValues={temporaryValues}
            cancelEdits={this.cancelInstitutionEdits}
            saveEdits={this.saveInstitutionEdits}
            errors={institutionErrors}
            loadingInstitutionEdits={loadingInstitutionEdits}
          />
        )}
        <div className="account__card">
          <h2 className="account__subheading">
            Your Information
          </h2>
          <button
            type="button"
            className="flat-link-from-button account__edit-card-button"
            onClick={this.toggleEditPersonalCard}
            disabled={isEditingPersonalCard}
          >
            <EditIcon disabled={isEditingPersonalCard} />
          </button>
          {personalErrors.length !== 0 && <InputErrors errors={personalErrors} />}
          <AccountInfoItem
            label="First Name"
            name="firstName"
            value={user.firstName}
            isEditing={isEditingPersonalCard}
            temporaryValue={temporaryValues.user.firstName}
            handleInputChange={this.handleUserInputChange}
          />
          <AccountInfoItem
            label="Last Name"
            name="lastName"
            value={user.lastName}
            isEditing={isEditingPersonalCard}
            temporaryValue={temporaryValues.user.lastName}
            handleInputChange={this.handleUserInputChange}
          />
          <AccountInfoItem
            label="Email"
            name="email"
            value={user.email}
            isEditing={isEditingPersonalCard}
            temporaryValue={temporaryValues.user.email}
            handleInputChange={this.handleUserInputChange}
          />
          <AccountInfoItem
            label="Department Name"
            name="department"
            value={user.department}
            isEditing={isEditingPersonalCard}
            temporaryValue={temporaryValues.user.department}
            handleInputChange={this.handleUserInputChange}
          />
          <AccountInfoItem
            label="Position Title"
            name="position"
            value={user.position}
            isEditing={isEditingPersonalCard}
            temporaryValue={temporaryValues.user.position}
            handleInputChange={this.handleUserInputChange}
          />
          {isEditingPersonalCard && (
            <div className="account__edit-buttons-wrapper">
              <button
                type="button"
                className="button button--reversed button--small"
                onClick={this.cancelPersonalEdits}
              >
                Cancel
              </button>
              <LoadingButton
                text="Save Changes"
                onClick={this.savePersonalEdits}
                loading={this.state.loadingPersonalEdits}
                largeButton={false}
              />
            </div>
          )}
          <div className="account__info-item">
            <dl>
              <dt className="account__info-label">
                Permissions
              </dt>
              <dd>
                {permissionLabel}
              </dd>
            </dl>
          </div>
          <div className="account__info-item">
            <div className="account__info-label">
              Change Password
            </div>
            <div>
              <button
                type="button"
                className="flat-link-from-button account__edit-item-button"
                onClick={toggleChangePasswordModal}
              >
                <EditIcon />
              </button>
            </div>
          </div>
        </div>
        <CSSTransition
          classNames="transition"
          timeout={200}
          in={showSoftAlert}
          mountOnEnter
          unmountOnExit
        >
          <SoftAlert errors={serverErrors} />
        </CSSTransition>
        {isAdmin && (
          <div className="account__card">
            <h2 className="account__subheading">
              Notification Emails
            </h2>
            <NotificationItem
              label="Invitation Accepted"
              checked={user.notificationPreferences
                ? user.notificationPreferences.invitationAccepted
                : false}
              onChange={this.handleNotificationChange}
              name="invitationAccepted"
            />
            <NotificationItem
              label="Account Permissions Change"
              checked={user.notificationPreferences
                ? user.notificationPreferences.accountPermissionsChange
                : false}
              onChange={this.handleNotificationChange}
              name="accountPermissionsChange"
            />
            <NotificationItem
              label="Assessment Completed"
              checked={user.notificationPreferences
                ? user.notificationPreferences.assessmentCompleted
                : false}
              onChange={this.handleNotificationChange}
              name="assessmentCompleted"
            />
            <NotificationItem
              label="Retake Assessment Reminder"
              checked={user.notificationPreferences
                ? user.notificationPreferences.retakeAssessmentReminder
                : false}
              includeMonths
              monthsValue={user.notificationPreferences
                ? user.notificationPreferences.retakeAssessmentReminderInterval
                : 12}
              onChange={this.handleNotificationChange}
              name="retakeAssessmentReminder"
            />
          </div>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    currentUserId: state.users.currentUserId,
    user: state.users.roster[state.users.currentUserId] || {},
    institution: state.users.institution,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatchUsersActions: bindActionCreators(usersActions, dispatch),
    dispatchInstitutionsActions: bindActionCreators(institutionsActions, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AccountCards);



// WEBPACK FOOTER //
// ./src/components/features/account/AccountCards.js